import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronRight, Mic, Brain, FileText, Lock } from "lucide-react";
import Button from "../Button";
import { useNavigate } from "react-router-dom";

const features = [
  {
    icon: <Mic className="w-10 h-10 text-[#1D1D1F]" />,
    title: "Record with ease.",
    description:
      "Start recording with a single tap. Our advanced AI captures and transcribes your medical notes in real-time.",
    footnote: "1",
    color: "bg-white",
  },
  {
    icon: <Brain className="w-10 h-10 text-[#1D1D1F]" />,
    title: "AI-powered transcription.",
    description:
      "Experience instant, accurate transcription powered by our advanced medical AI. Focus on your patients, not your notes.",
    footnote: "2",
    color: "bg-white",
  },
  {
    icon: <FileText className="w-10 h-10 text-[#1D1D1F]" />,
    title: "Smart medical coding.",
    description:
      "Automatically extract and suggest ICD and CPT codes from your recordings. Save time with intelligent code matching.",
    footnote: "3",
    color: "bg-white",
  },
  {
    icon: <Lock className="w-10 h-10 text-[#1D1D1F]" />,
    title: "Secure and compliant.",
    description:
      "Your data is protected with enterprise-grade security. HIPAA-compliant storage and processing for peace of mind.",
    footnote: "4",
    color: "bg-white",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export const WelcomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToSlide = (index: number) => {
    if (containerRef.current) {
      const slideWidth = containerRef.current.offsetWidth; // Dynamic width for responsiveness
      containerRef.current.scrollTo({
        left: slideWidth * index,
        behavior: "smooth",
      });
      setCurrentSlide(index);
    }
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const slideWidth = containerRef.current.offsetWidth;
      const scrollPosition = containerRef.current.scrollLeft;
      const newSlide = Math.round(scrollPosition / slideWidth);
      setCurrentSlide(newSlide);
    }
  };

  return (
    <motion.div
      className="min-h-screen w-full bg-[#F5F5F7] overflow-x-hidden"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="py-[120px] px-4 md:px-8 max-w-[1220px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-start justify-between mb-[80px] gap-8">
          <motion.div variants={itemVariants} className="max-w-[680px]">
            <h1 className="text-[40px] md:text-[56px] font-sf-pro font-semibold text-[#1D1D1F] leading-[1.07] tracking-[-0.005em] mb-6">
              Transform your medical documentation.
            </h1>
            <p className="text-[18px] md:text-[21px] font-sf-pro text-[#1D1D1F] leading-[1.381] tracking-[.011em]">
              Powerful AI assistance that understands medical terminology.
              Designed for healthcare professionals.
            </p>
          </motion.div>
          <motion.div
            variants={itemVariants}
            className="flex flex-col items-center md:items-end gap-4"
          >
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate("/visit")}
              className="w-full md:w-auto h-[50px] px-8 bg-[#06c] hover:bg-[#06c]/90 flex items-center justify-center gap-2 rounded-full shadow-lg"
            >
              <Mic className="w-5 h-5" />
              Start Recording
            </Button>
          </motion.div>
        </div>

        <div className="relative w-full overflow-visible px-0">
          {/* Cards Container */}
          <motion.div
            ref={containerRef}
            variants={itemVariants}
            className="flex flex-nowrap gap-4 overflow-x-auto pb-8 snap-x snap-mandatory hide-scrollbar"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
              paddingLeft: "4px",
              paddingRight: "300px", // Large fixed padding to ensure last card is visible
              width: "100vw",
              maxWidth: "100vw",
            }}
            onScroll={handleScroll}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className={`${feature.color} rounded-[24px] p-5 md:p-6 relative group cursor-pointer overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 min-w-[260px] md:min-w-[320px] snap-center`}
                whileHover={{ y: -2, scale: 1.005 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="flex flex-col h-full">
                  <div className="text-[32px] md:text-[40px] mb-4 text-[#1D1D1F]">
                    {feature.icon}
                  </div>

                  <h3 className="text-[20px] md:text-[24px] font-semibold text-[#1D1D1F] leading-[1.1667] tracking-[.009em] mb-2">
                    {feature.title}
                  </h3>

                  <div className="flex-grow">
                    <p className="text-[14px] md:text-[16px] text-[#1D1D1F] leading-[1.4706] tracking-[-.022em]">
                      {feature.description}
                      <sup className="text-[#6E6E73] text-[10px] ml-0.5">
                        {feature.footnote}
                      </sup>
                    </p>
                  </div>

                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Navigation Controls - Bottom Right */}
          <div className="hidden md:flex items-center gap-3 absolute -bottom-4 right-0">
            <button
              onClick={() => {
                const newSlide = Math.max(0, currentSlide - 1);
                scrollToSlide(newSlide);
              }}
              className="w-[36px] h-[36px] rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentSlide === 0}
            >
              <ChevronRight className="w-4 h-4 text-[#1D1D1F] rotate-180" />
            </button>
            <button
              onClick={() => {
                const newSlide = Math.min(features.length - 1, currentSlide + 1);
                scrollToSlide(newSlide);
              }}
              className="w-[36px] h-[36px] rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentSlide === features.length - 1}
            >
              <ChevronRight className="w-4 h-4 text-[#1D1D1F]" />
            </button>
          </div>
        </div>

        {/* Navigation Dots - Only shown on mobile */}
        <div className="flex md:hidden justify-center gap-2 mt-8">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                currentSlide === index
                  ? "bg-[#1D1D1F] w-4"
                  : "bg-[#1D1D1F]/20 hover:bg-[#1D1D1F]/40"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default WelcomeScreen;
